import { useState, useMemo } from "react";
import { IntervalPreset, DataInterval } from "../../ChartTopFilters";
import { subDays, subMonths } from "date-fns";

interface DateRangeConfig {
  start: Date;
  end: Date;
}

interface ChartFiltersState {
  selectedPreset: IntervalPreset;
  dateRange: DateRangeConfig;
  interval: DataInterval;
  compareTo: "previousYear" | null;
}

interface ChartFiltersActions {
  setPreset: (preset: IntervalPreset) => void;
  setDateRange: (range: DateRangeConfig) => void;
  setInterval: (interval: DataInterval) => void;
  setCompareTo: (compareTo: "previousYear" | null) => void;
}

interface UseChartFiltersOptions {
  initialState?: Partial<ChartFiltersState>;
}

const calculateDateRangeForPreset = (
  preset: IntervalPreset,
  currentRange: DateRangeConfig,
): DateRangeConfig => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "7d":
      return { start: subDays(today, 6), end: today };
    case "30d":
      return { start: subDays(today, 30), end: today };
    case "3m":
      return { start: subMonths(today, 2), end: today };
    case "6m":
      return { start: subMonths(today, 5), end: today };
    case "12m":
      return { start: subMonths(today, 12), end: today };
    case "currentCycle": {
      const currentYear = now.getFullYear();
      const cycleYear = now.getMonth() >= 7 ? currentYear : currentYear - 1;
      return {
        start: new Date(cycleYear, 7, 1),
        end: new Date(),
      };
    }
    case "custom":
    default:
      return currentRange;
  }
};

const getAutoInterval = (start: Date, end: Date): DataInterval => {
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 30) return "daily";
  if (diffDays <= 90) return "weekly";
  return "monthly";
};

const getDefaultState = (): ChartFiltersState => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const cycleYear = now.getMonth() >= 7 ? currentYear : currentYear - 1;

  return {
    selectedPreset: "currentCycle",
    dateRange: {
      start: new Date(cycleYear, 7, 1),
      end: new Date(),
    },
    interval: "monthly",
    compareTo: null,
  };
};

/**
 * Internal hook for ChartCard filter state management
 * Not intended for reuse outside of ChartCard
 */
export const useChartFilters = (options: UseChartFiltersOptions = {}) => {
  const { initialState } = options;

  const [state, setState] = useState<ChartFiltersState>(() => ({
    ...getDefaultState(),
    ...initialState,
  }));

  const actions: ChartFiltersActions = useMemo(
    () => ({
      setPreset: (preset: IntervalPreset) => {
        setState((prev) => {
          const newRange =
            preset !== "custom"
              ? calculateDateRangeForPreset(preset, prev.dateRange)
              : prev.dateRange;

          const autoInterval =
            preset !== "custom"
              ? getAutoInterval(newRange.start, newRange.end)
              : prev.interval;

          return {
            ...prev,
            selectedPreset: preset,
            dateRange: newRange,
            interval: autoInterval,
          };
        });
      },

      setDateRange: (range: DateRangeConfig) => {
        setState((prev) => ({
          ...prev,
          dateRange: range,
          selectedPreset: "custom" as IntervalPreset,
          interval: getAutoInterval(range.start, range.end),
        }));
      },

      setInterval: (interval: DataInterval) => {
        setState((prev) => ({ ...prev, interval }));
      },

      setCompareTo: (compareTo: "previousYear" | null) => {
        setState((prev) => ({ ...prev, compareTo }));
      },
    }),
    [],
  );

  const filtersCount = useMemo(() => {
    let count = 0;
    if (state.selectedPreset === "custom") count++;
    if (state.compareTo) count++;
    return count;
  }, [state.selectedPreset, state.compareTo]);

  return { state, actions, filtersCount };
};
